import { chartingQueryKeys, invalidateChartingFavorites } from '@/features/charting-next/api/chartingCache';
import {
    addTickerToChartingFavorites,
    createChartingFavoriteFolder,
    deleteChartingFavoriteFolder,
    fetchChartingFavoriteFolders,
    removeTickerFromChartingFavorites,
    updateChartingFavoriteFolder
} from '@/features/charting-next/api/chartingFavoritesRepository';
import { useRemoteResource } from '@/features/charting-next/services/useRemoteResource';
import { ref } from '@vue/composition-api';

const createRandomFolderColor = () => {
    const palette = ['#0ea5e9', '#ef4444', '#22c55e', '#8b5cf6', '#f97316', '#14b8a6'];

    return palette[Math.floor(Math.random() * palette.length)];
};

export function useChartingFavorites({ sessionStore, capabilities, selectedStock, refreshCollection, patchCollectionFavorite = () => false }) {
    const isMutatingFavorites = ref(false);
    const favoriteMutationError = ref('');

    const favoriteFoldersResource = useRemoteResource({
        key: () => chartingQueryKeys.favorites(sessionStore.uid),
        loader: fetchChartingFavoriteFolders,
        ttl: 30000,
        enabled: () => sessionStore.isAuthenticated && capabilities.value.canUseFavorites
    });

    const refreshFavoriteState = async ({ refreshFoldersOnly = false } = {}) => {
        const tasks = [invalidateChartingFavorites(sessionStore.uid)];

        if (!refreshFoldersOnly) {
            tasks.push(refreshCollection());
        }

        await Promise.all(tasks);
    };

    const toggleFavoriteFolderForStock = async (stock, folderName) => {
        if (!stock?.ticker || !folderName) {
            return;
        }

        isMutatingFavorites.value = true;
        favoriteMutationError.value = '';

        try {
            const isAlreadyFavorite = (stock.folderNames || []).includes(folderName);

            if (isAlreadyFavorite) {
                await removeTickerFromChartingFavorites({
                    ticker: stock.ticker,
                    direction: stock.direction,
                    folderName
                });
            } else {
                await addTickerToChartingFavorites({
                    ticker: stock.ticker,
                    direction: stock.direction,
                    folderName
                });
            }

            const patched = patchCollectionFavorite({
                ticker: stock.ticker,
                direction: stock.direction,
                folderName,
                isFavorite: !isAlreadyFavorite
            });
            await refreshFavoriteState({ refreshFoldersOnly: patched });
        } catch (error) {
            favoriteMutationError.value = error.message || 'No se pudo actualizar favoritos.';
        } finally {
            isMutatingFavorites.value = false;
        }
    };

    const toggleFavoriteFolder = (folderName) => toggleFavoriteFolderForStock(selectedStock.value, folderName);

    const createFavoriteFolder = async (name, color = createRandomFolderColor()) => {
        const normalizedName = String(name || '').trim();

        if (!normalizedName) {
            return;
        }

        await createChartingFavoriteFolder({
            name: normalizedName,
            color
        });

        await refreshFavoriteState({
            refreshFoldersOnly: true
        });
    };

    const renameFavoriteFolder = async ({ name, newName, newColor }) => {
        await updateChartingFavoriteFolder({
            name,
            newName,
            newColor
        });

        await refreshFavoriteState();
    };

    const removeFavoriteFolder = async (name) => {
        await deleteChartingFavoriteFolder(name);
        await refreshFavoriteState();
    };

    return {
        favoriteFoldersResource,
        isMutatingFavorites,
        favoriteMutationError,
        toggleFavoriteFolder,
        toggleFavoriteFolderForStock,
        createFavoriteFolder,
        renameFavoriteFolder,
        removeFavoriteFolder
    };
}
